<template>
  <DynamicComponentProductionElement
    v-if="rootElementId"
    :dynamic-component-id="dynamicComponentId"
    :element-id="rootElementId"
    :theme-id="dynamicComponent.themeId"
    :theme-mode="themeMode"
    :is-production="isProduction"
  />
</template>

<script>
import DynamicComponent from "%/dynamic-component-editor/classes/dynamic-component";
import DynamicComponentProductionElement
  from "%/dynamic-component-editor/components/DynamicComponentProductionElement.vue";
import { createEventBus } from "%/classes/event-bus";
import ScopedProvidesContextsMixin from "%/dynamic-component-editor/mixins/scoped-provides-contexts-mixin";

// Monotonic per-render id for instance-scoped event keys — a stable
// alternative to Vue's undocumented internal `this.$.uid`.
let nextDcInstanceId = 0;

// Production-mode equivalent of DynamicComponentBuilderComponent. Given a
// DC id, reads the root element id from whichever store is active (staging
// by default, production when `isProduction` is set) and hands off to one
// DynamicComponentProductionElement. Every descendant looks up its own
// entry by id, so nothing but the DC id + mode flows through props.
export default {
  name: "DynamicComponentProductionComponent",
  components: { DynamicComponentProductionElement },
  mixins: [ScopedProvidesContextsMixin],
  // Outermost instance creates the page's event bus; nested DCs inherit it,
  // so header→page chains work while separate page renders stay isolated.
  inject: {
    inheritedPageEventBus: {
      from: "dcPageEventBus",
      default: null,
    },
  },
  provide() {
    const isOutermostPageRender = this.inheritedPageEventBus === null;
    const pageEventBus = isOutermostPageRender ? createEventBus() : this.inheritedPageEventBus;
    return {
      useExampleData: () => this.useExampleData,
      dcPageEventBus: pageEventBus,
      // Unique per render — instance-scoped events qualify names with this so
      // two drops of the same reusable component never cross-fire.
      dcInstanceKey: `dci-${nextDcInstanceId++}`,
      // XXX: The id of the DC being rendered, for elements that must NAME their
      // XXX: owning DC in an error. dynamicComponentInstance is a builder-store
      // XXX: read and is undefined on compiled surfaces -- exactly where the
      // XXX: error matters -- so a diagnostic cannot rely on it.
      hostDynamicComponentId: this.dynamicComponentId,
    };
  },
  props: {
    dynamicComponentId: {
      type: [String, Number],
      required: true,
    },
    isProduction: {
      type: Boolean,
      required: false,
      default: false,
    },
    // Preview-modal-only flag. When true the descendant elements that
    // consume a page-scoped entity (Story / Event / DigitalProduct /
    // …) read their example shape instead of issuing a real-entity
    // fetch. Production + staging set this to false so the page renders
    // the actual record keyed by URL. Provided to descendants via
    // inject so each element mixin can branch its own
    // example-vs-real read off the same flag without prop-drilling.
    useExampleData: {
      type: Boolean,
      required: false,
      default: false,
    },
    themeMode: {
      type: String,
      required: false,
      default: "",
    },
  },
  computed: {
    dynamicComponent() {
      return DynamicComponent.get(this.dynamicComponentId, this.isProduction) ?? {};
    },
    rootElementId() {
      return this.dynamicComponent?.rootElementId ?? null;
    },
  },
  created() {
    // Only NESTED renders resolve provides-contexts into a scope — the outermost
    // page's resolve globally; example-data (preview) renders never resolve pks.
    const isOutermostPageRender = this.inheritedPageEventBus === null;
    if (!isOutermostPageRender && !this.useExampleData) {
      this.resolveScopedProvidedContexts(this.dynamicComponent, String(this.dynamicComponentId));
    }
  },
};
</script>
